import { useEffect, useRef, useCallback } from 'react';

/**
 * useAutoSaveDraft Hook
 * 
 * Auto-saves template draft to localStorage with debounce (5 seconds)
 * Supports multiple drafts - each draft has unique ID
 * Draft format: { id: uuid, template: {...}, questions: [...], timestamp: Date.now() }
 * Draft expires after 7 days
 * 
 * Usage:
 * const { clearDraft } = useAutoSaveDraft(templateState, questions, draftId);
 * 
 * @param {Object} templateState - Current template state (title, description, category, contentHtml, etc.)
 * @param {Array} questions - Current questions array
 * @param {string} draftId - Unique draft ID (e.g., 'edit-123' or 'new-uuid')
 * @returns {Object} { clearDraft: function } - Function to manually clear draft
 */
const useAutoSaveDraft = (templateState, questions, draftId) => {
  const timeoutRef = useRef(null);
  const lastSavedRef = useRef(null);

  // Get all drafts from localStorage
  const getAllDrafts = useCallback(() => {
    try {
      const draftsJson = localStorage.getItem('template-drafts-list');
      if (!draftsJson) return [];
      return JSON.parse(draftsJson);
    } catch (error) {
      console.error('Failed to load drafts list:', error);
      return [];
    }
  }, []);

  // Save all drafts to localStorage
  const saveAllDrafts = useCallback((drafts) => {
    try {
      localStorage.setItem('template-drafts-list', JSON.stringify(drafts));
    } catch (error) {
      console.error('Failed to save drafts list:', error);
    }
  }, []);

  // Clear draft function
  const clearDraft = useCallback(() => {
    if (!draftId) return;
    
    // Remove from drafts list only (no individual draft storage)
    const drafts = getAllDrafts();
    const updatedDrafts = drafts.filter(d => d.id !== draftId);
    saveAllDrafts(updatedDrafts);
    
    lastSavedRef.current = null;
  }, [draftId, getAllDrafts, saveAllDrafts]);

  // Auto-save function
  const saveDraft = useCallback(() => {
    if (!draftId) return;

    // Don't save if nothing has changed
    const currentState = JSON.stringify({ templateState, questions });
    if (currentState === lastSavedRef.current) {
      return;
    }

    // Create draft object
    const draft = {
      id: draftId, // Include ID in draft object
      template: {
        title: templateState.title || '',
        description: templateState.description || '',
        category: templateState.category || 'Diğer',
        contentHtml: templateState.contentHtml || '',
        thumbnail: templateState.thumbnail || '',
        isPublic: templateState.isPublic !== undefined ? templateState.isPublic : true
      },
      questions: questions || [],
      timestamp: Date.now()
    };

    // Save to localStorage
    try {
      // Only save to drafts list (no individual draft storage)
      const drafts = getAllDrafts();
      const existingIndex = drafts.findIndex(d => d.id === draftId);
      
      if (existingIndex >= 0) {
        // Update existing draft
        drafts[existingIndex] = draft;
      } else {
        // Add new draft
        drafts.push(draft);
      }
      
      // Sort by timestamp (newest first)
      drafts.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
      
      // Save updated list
      saveAllDrafts(drafts);
      
      lastSavedRef.current = currentState;

    } catch (error) {
      console.error('Failed to save draft:', error);
    }
  }, [templateState, questions, draftId, getAllDrafts, saveAllDrafts]);

  // Auto-save with debounce (5 seconds)
  useEffect(() => {
    // Clear previous timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Only save if there's meaningful content
    const hasContent = 
      (templateState?.title && templateState.title.trim() !== '') ||
      (templateState?.contentHtml && templateState.contentHtml.trim() !== '') ||
      (questions && Array.isArray(questions) && questions.length > 0);

    if (!hasContent || !draftId) {
      return;
    }

    // Set new timeout (3 seconds debounce)
    timeoutRef.current = setTimeout(() => {
      saveDraft();
    }, 3000);

    // Cleanup
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [templateState, questions, saveDraft, draftId]);

  return { clearDraft };
};

/**
 * Get all drafts from localStorage (for new templates only)
 * 
 * @returns {Array} Array of draft objects, sorted by timestamp (newest first)
 */
export const getAllDrafts = () => {
  try {
    const draftsJson = localStorage.getItem('template-drafts-list');
    if (!draftsJson) return [];

    const drafts = JSON.parse(draftsJson);
    
    // Check for expired drafts (7 days = 7 * 24 * 60 * 60 * 1000 ms)
    const DRAFT_EXPIRY_MS = 7 * 24 * 60 * 60 * 1000;
    const now = Date.now();
    
    const validDrafts = drafts.filter(draft => {
      if (!draft.timestamp) return false;
      if ((now - draft.timestamp) > DRAFT_EXPIRY_MS) {
        // Draft expired, remove it (only from list, no individual storage)
        return false;
      }
      return true;
    });
    
    // Update list if any drafts were removed
    if (validDrafts.length !== drafts.length) {
      localStorage.setItem('template-drafts-list', JSON.stringify(validDrafts));
    }
    
    // Sort by timestamp (newest first)
    return validDrafts.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
  } catch (error) {
    console.error('Failed to load drafts list:', error);
    return [];
  }
};

/**
 * Get single draft from localStorage
 * 
 * @param {string} draftId - Draft ID
 * @returns {Object|null} Draft object or null if not found/expired
 */
export const getDraft = (draftId) => {
  if (!draftId) return null;

  try {
    // Get from drafts list only (no individual draft storage)
    const drafts = getAllDrafts();
    const draft = drafts.find(d => d.id === draftId);
    return draft || null;
  } catch (error) {
    console.error('Failed to load draft:', error);
    return null;
  }
};

/**
 * Check if draft exists
 * 
 * @param {string} draftId - Draft ID
 * @returns {boolean} True if draft exists and is not expired
 */
export const hasDraft = (draftId) => {
  return getDraft(draftId) !== null;
};

/**
 * Delete a specific draft
 * 
 * @param {string} draftId - Draft ID to delete
 */
export const deleteDraft = (draftId) => {
  if (!draftId) return;
  
  try {
    // Remove from drafts list only (no individual draft storage)
    const drafts = getAllDrafts();
    const updatedDrafts = drafts.filter(d => d.id !== draftId);
    localStorage.setItem('template-drafts-list', JSON.stringify(updatedDrafts));
  } catch (error) {
    console.error('Failed to delete draft:', error);
  }
};

/**
 * Delete all drafts for a specific template ID
 * Used when template is successfully saved
 * 
 * @param {string} templateId - Template ID (e.g., '123' or ObjectId string)
 */
export const deleteDraftsByTemplateId = (templateId) => {
  if (!templateId) return;
  
  try {
    const drafts = getAllDrafts();
    // Remove all drafts that match this template ID (e.g., 'edit-123' or any draft with this templateId)
    const updatedDrafts = drafts.filter(d => {
      // Check if draft ID starts with 'edit-{templateId}'
      if (d.id && d.id.startsWith(`edit-${templateId}`)) {
        return false;
      }
      // Also check if draft has templateId property that matches
      if (d.templateId && d.templateId === templateId) {
        return false;
      }
      return true;
    });
    
    if (updatedDrafts.length < drafts.length) {
      localStorage.setItem('template-drafts-list', JSON.stringify(updatedDrafts));
      console.log(`Deleted ${drafts.length - updatedDrafts.length} draft(s) for template ${templateId}`);
    }
  } catch (error) {
    console.error('Failed to delete drafts by template ID:', error);
  }
};

export default useAutoSaveDraft;

